import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { track } from '@/lib/analytics';
import { FiCopy, FiCheck } from 'react-icons/fi';
import {
  Button,
  Card,
  Input,
  PhoneNumberInput,
  normalizeUsPhoneNumber,
} from '@/components';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { apiJson, ApiError } from '@/lib/api';

const TOTAL_STEPS = 11;
import { setCoparentInviteSkipped } from '@/lib/storage';
import type {
  InviteListResponse,
  InviteResponse,
  UserResponse,
} from '@/lib/types';
import { getFromUserName } from '@/lib/types';

function InviteOnboardingPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track screen view
  useEffect(() => {
    track('Coparent Invite Screen');
  }, []);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<InviteResponse | null>(
    null
  );

  // Fetch invites to check for pending received invites
  const invitesQuery = useQuery({
    queryKey: ['invites'],
    queryFn: async () => {
      try {
        return await apiJson<InviteListResponse>(getToken, '/v1/invites');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return { received: [], sent: [] } as InviteListResponse;
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
  });

  // Fetch current user so we can prevent inviting their own phone number.
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
  });

  // Check for pending received invites
  useEffect(() => {
    if (invitesQuery.data) {
      const pendingReceivedInvites = invitesQuery.data.received.filter(
        (invite) => !invite.accepted_user_id && !invite.rejected_user_id
      );
      if (pendingReceivedInvites.length > 0) {
        setPendingInvite(pendingReceivedInvites[0]);
        setShowAcceptModal(true);
      }
    }
  }, [invitesQuery.data]);

  // Accept invite mutation
  const acceptInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      return apiJson(getToken, `/v1/invites/${inviteId}/accept`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setCoparentInviteSkipped(true);
      setShowAcceptModal(false);
      setPendingInvite(null);
      navigate({ to: '/app/settings' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't accept the invite. Please try again.";
      setError(message);
    },
  });

  // Reject invite mutation
  const rejectInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      return apiJson(getToken, `/v1/invites/${inviteId}/reject`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      setShowAcceptModal(false);
      setPendingInvite(null);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't reject the invite. Please try again.";
      setError(message);
    },
  });

  // Create invite mutation
  const inviteMutation = useMutation({
    mutationFn: async () => {
      const phoneNumber = normalizeUsPhoneNumber(phone);
      if (!phoneNumber) {
        throw new Error('Please provide a valid phone number.');
      }
      if (!name.trim()) {
        throw new Error('Please enter a name.');
      }
      if (
        userQuery.data?.phone_number &&
        phoneNumber === userQuery.data.phone_number
      ) {
        throw new Error("You can't send an invite to your own phone number.");
      }
      const payload = { to_name: name.trim(), to_phone_number: phoneNumber };
      return apiJson(getToken, '/v1/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setCoparentInviteSkipped(true);
      navigate({ to: '/app/onboarding/children' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't send the invite. Please try again.";
      setError(message);
    },
  });

  const handleSendInvite = () => {
    const phoneNumber = normalizeUsPhoneNumber(phone);
    if (!phoneNumber) {
      setError('Please provide a valid phone number.');
      return;
    }
    if (!name.trim()) {
      setError('Please enter a name.');
      return;
    }
    if (
      userQuery.data?.phone_number &&
      phoneNumber === userQuery.data.phone_number
    ) {
      setError("You can't send an invite to your own phone number.");
      return;
    }
    setError('');
    inviteMutation.mutate();
  };

  const handleSkip = () => {
    setCoparentInviteSkipped(true);
    navigate({ to: '/app/onboarding/children' });
  };

  const handleCopyLink = async () => {
    // Generate a shareable link (you could make this a real invite link)
    const inviteLink = `${window.location.origin}/auth`;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  // Loading state
  if (invitesQuery.isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  // Show accept invite modal if there's a pending invite
  if (showAcceptModal && pendingInvite) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center p-6'>
        <Card className='max-w-md w-full'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Accept invite?
          </h2>
          <p className='text-gray-500 mb-6'>
            {getFromUserName(pendingInvite)} invited you to join their
            household. You'll be able to share expenses and payments together.
          </p>

          {error && (
            <p className='text-sm text-red-500 text-center mb-4'>{error}</p>
          )}

          <div className='flex gap-3'>
            <Button
              variant='secondary'
              fullWidth
              onClick={() =>
                pendingInvite && rejectInviteMutation.mutate(pendingInvite.id)
              }
              disabled={
                acceptInviteMutation.isPending || rejectInviteMutation.isPending
              }
            >
              Reject
            </Button>
            <Button
              fullWidth
              onClick={() =>
                pendingInvite && acceptInviteMutation.mutate(pendingInvite.id)
              }
              disabled={
                acceptInviteMutation.isPending || rejectInviteMutation.isPending
              }
              loading={acceptInviteMutation.isPending}
            >
              {acceptInviteMutation.isPending ? 'Accepting…' : 'Accept'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      <OnboardingProgress currentStep={9} totalSteps={TOTAL_STEPS} />
      <div className='max-w-md mx-auto px-6 py-12'>
        {/* Title */}
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-bold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Invite your co-parent
          </h1>
          <p
            className='text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Harbor helps co-parents track shared expenses and payments. Send an
            invite to your co-parent so you can start managing costs together.
          </p>
        </div>

        {/* Form */}
        <Card>
          <div className='space-y-4'>
            <Input
              label='Co-parent name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Taylor'
              disabled={inviteMutation.isPending}
              autoFocus
            />

            <PhoneNumberInput
              label='Phone number'
              value={phone}
              onChangeText={setPhone}
              disabled={inviteMutation.isPending}
            />

            {error && (
              <p className='text-sm text-red-500 text-center'>{error}</p>
            )}

            <div className='pt-2'>
              <Button
                fullWidth
                onClick={handleSendInvite}
                disabled={inviteMutation.isPending}
                loading={inviteMutation.isPending}
              >
                {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </div>

            {/* Copy link option */}
            <button
              type='button'
              onClick={handleCopyLink}
              className='w-full flex items-center justify-center gap-2 py-3 text-primary hover:text-primary-hover transition-colors'
            >
              {copied ? (
                <>
                  <FiCheck size={18} />
                  <span className='font-semibold'>Link copied!</span>
                </>
              ) : (
                <>
                  <FiCopy size={18} />
                  <span className='font-semibold'>
                    Copy invite link instead
                  </span>
                </>
              )}
            </button>

            <Button
              variant='secondary'
              fullWidth
              onClick={handleSkip}
              disabled={inviteMutation.isPending}
            >
              Not now
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/invite')({
  component: InviteOnboardingPage,
});
